'use client';

import { useRef, useState } from 'react';
import {
	capturePostHogEvent,
	identifyKlaviyoProfile,
	identifyPostHogProfile,
} from '@/lib/analytics/client';

interface NewsletterFormProps {
	placeholder?: string;
	buttonLabel?: string;
	source?: string;
}

export function NewsletterForm({
	placeholder = 'Enter your email',
	buttonLabel = 'Subscribe',
	source = 'footer-newsletter',
}: NewsletterFormProps) {
	const emailRef = useRef<HTMLInputElement>(null);
	const [submitted, setSubmitted] = useState(false);

	return (
		<form
			className="mt-6 flex gap-2"
			onSubmit={(e) => {
				e.preventDefault();
				const email = emailRef.current?.value?.trim();
				if (!email) return;

				setSubmitted(true);

				capturePostHogEvent('newsletter_signup_submitted', {
					form_location: source,
					has_email: true,
				});

				// Identify this profile in Klaviyo onsite so subsequent events attribute correctly.
				// Newsletter opt-in is explicit marketing consent — bypass the cookie-banner
				// gate so Klaviyo can merge the anonymous profile into this one, backfilling
				// prior Viewed Product / Added to Cart / Started Checkout events.
				identifyKlaviyoProfile({ email, explicitOptIn: true });
				identifyPostHogProfile({
					email,
					source,
				});

				// Server-side subscribe via Klaviyo's subscription-bulk-create API. Errors are
				// swallowed server-side so the form always shows success.
				void fetch('/api/newsletter/subscribe', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ email, source }),
					keepalive: true,
				}).catch(() => {
					// Ignore transport failures.
				});
			}}
		>
			{submitted ? (
				<p className="flex-1 py-2.5 text-sm text-neutral-400">Thanks for subscribing!</p>
			) : (
				<>
					<input
						ref={emailRef}
						type="email"
						placeholder={placeholder}
						className="flex-1 rounded-md border border-neutral-700 bg-neutral-900 px-4 py-2.5 text-sm text-white placeholder:text-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
						required
					/>
					<button
						type="submit"
						className="rounded-md bg-white px-6 py-2.5 text-sm font-semibold text-neutral-900 transition-colors hover:bg-neutral-200"
					>
						{buttonLabel}
					</button>
				</>
			)}
		</form>
	);
}
